import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useDebounce } from "use-debounce";
import { EntityType, UnitService, UnitVM, AccessLevelEnum } from "@propertelligent/client-api";
import { infiniteScrollHook } from "../../hooks/infiniteScrollHook";
import UnitTopHeader from "../../newComponents/listings/top-listing-headers/unit-listing-top-header";
import UnitCard from "../../newComponents/listings/listing-cards/unit";
import AddUnitForm from "../../newComponents/unit/AddUnitForm";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import UnitDetails2 from "../../newComponents/unit/UnitDetails";
import apiHelper from "../../utils/apiHelper";
import { usePermissions } from "../../hooks/usePermissions";
import NoAccess from "../NoAccess";
import { PermissionGuard } from "../../newComponents/PermissionGuard";
import { UserLoginData } from "../../components/common/charts/Enums";
import { ALL_FILTER_KEY, resolveAllFilterCount, useAggregateFilterCounts } from "../../hooks/useAggregateFilterCounts";
import {
  getApiUnitOrganizationlistCounts,
  getApiUnitPropertylistCounts,
} from "../../services/listFilterCounts.service";
import { escapeDynamicLinqStringLiteral } from "../../utils/dynamicLinq";

interface UnitsProps {
  isEntity?: boolean;
  entityTypeId?: number;
  entityId?: number;
  className?: string;
  formId?: string;
  onUnitAdded?: () => void;
  unitRefreshTrigger?: number;
}

const Units = ({
  isEntity,
  entityTypeId,
  entityId,
  onUnitAdded,
  unitRefreshTrigger,
}: UnitsProps) => {
  const { hasAccess, canWrite } = usePermissions();
  if (!hasAccess("Unit", 1)) return <NoAccess />;

  const [searchTerm, setSearchTerm] = useState("");
  const [fullyQualifiedFilter, setFullyQualifiedFilter] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [activeFilter, setActiveFilter] = useState(ALL_FILTER_KEY);
  const [isModalOpen, setModalOpen] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState<UnitVM | undefined>(undefined);
  const [hasAnyUnits, setHasAnyUnits] = useState(false);

  const activeFilterRef = useRef(activeFilter);
  useEffect(() => { activeFilterRef.current = activeFilter; }, [activeFilter]);

  const location = useLocation();
  const navigate = useNavigate();

  const buildQFilter = useCallback((filter: string, search: string): string | null => {
    const parts: string[] = [];
    if (filter === "Vacant") parts.push("!Tenants.Any()");
    if (filter === "Occupied") parts.push("Tenants.Any()");
    if (search) parts.push(`UnitNumber.ToLower().Contains("${escapeDynamicLinqStringLiteral(search.toLowerCase())}")`);
    return parts.length ? parts.join(" && ") : null;
  }, []);

  const [debouncedSearchTerm] = useDebounce(searchTerm, 500);

  const debouncedSearchRef = useRef(debouncedSearchTerm);
  useEffect(() => { debouncedSearchRef.current = debouncedSearchTerm; }, [debouncedSearchTerm]);
  const searchOnlyFilter = useMemo(() => {
    return debouncedSearchTerm
      ? `UnitNumber.ToLower().Contains("${escapeDynamicLinqStringLiteral(debouncedSearchTerm.toLowerCase())}")`
      : null;
  }, [debouncedSearchTerm]);

  const getUnitListFn = useCallback(() => {
    if (isEntity && entityTypeId !== undefined && entityId !== undefined && entityTypeId === EntityType.Unit) {
      return (
        _: number,
        pageSize?: number, pageNumber?: number, skipValue?: number,
        filterEntityTypeId?: any, filterEntityIds?: any,
        fullyQualifiedFilters?: string, fullyQualifiedSorts?: string,
        ...rest: any[]
      ) =>
        UnitService.getApiUnitPropertylist(
          entityId, pageSize, pageNumber, skipValue,
          filterEntityTypeId, filterEntityIds,
          fullyQualifiedFilters, fullyQualifiedSorts, ...rest
        );
    }
    return UnitService.getApiUnitOrganizationlist;
  }, [isEntity, entityTypeId, entityId]);

  const { rows, loading, hasMore, fetchData, currentPage, aggregateValues, rowCount } =
    infiniteScrollHook({
      httpFetchDataFn: getUnitListFn(),
      FullyQualifiedFilters: fullyQualifiedFilter,
      pageSize: 10,
      FullyQualifiedSorts: `UnitNumber ${sortDirection}`,
    });

  useEffect(() => {
    setFullyQualifiedFilter(buildQFilter(activeFilter, debouncedSearchTerm));
  }, [activeFilter, debouncedSearchTerm, buildQFilter]);
  const scopeId = useMemo(() => {
    const orgIdStr = sessionStorage.getItem(UserLoginData.organization_id);
    const tenantIdStr = sessionStorage.getItem(UserLoginData.tenant_id);
    const orgId = orgIdStr ? parseInt(orgIdStr) : 0;
    const tenantId = tenantIdStr ? parseInt(tenantIdStr) : 0;
    return isEntity && entityId ? entityId : orgId || tenantId;
  }, [entityId, isEntity]);

  const fetchCounts = useCallback(async (filter: string | null) => {
    const countsApi = isEntity && entityTypeId !== undefined && entityId !== undefined && entityTypeId === EntityType.Unit
      ? getApiUnitPropertylistCounts
      : getApiUnitOrganizationlistCounts;

    const res = await apiHelper(
      countsApi,
      { showNotification: false },
      scopeId,
      1,
      1,
      undefined,
      undefined,
      undefined,
      filter,
      undefined,
    );

    return res?.data?.aggregateValues ?? undefined;
  }, [entityId, entityTypeId, isEntity, scopeId]);

  const {
    aggregateValues: unitCounts,
    baselineAggregateValues,
    refreshCounts,
    getVisibleOptions,
  } = useAggregateFilterCounts({
    fetchCounts,
    searchOnlyFilter,
    scopeKey: `units:${isEntity ? `entity:${entityId}:${entityTypeId}` : `org:${scopeId}`}`,
  });

  const currentTotalCount = useMemo(
    () => resolveAllFilterCount(unitCounts),
    [unitCounts],
  );

  const visibleFilterOptions = useMemo(
    () =>
      getVisibleOptions([
        { title: ALL_FILTER_KEY, count: currentTotalCount },
        { title: "Vacant" },
        { title: "Occupied" },
      ]),
    [currentTotalCount, getVisibleOptions],
  );

  useEffect(() => {
    // Tolerate a failed/in-flight counts fetch (apiHelper swallows the error and returns
    // `undefined`) by also trusting the row-list's own count -- otherwise a single failing
    // counts call would hide the whole page (header, pills, add button) behind the empty
    // state even though the rows loaded fine.
    setHasAnyUnits(resolveAllFilterCount(baselineAggregateValues) > 0 || rowCount > 0);
  }, [baselineAggregateValues, rowCount]);

  // Same unstable-`fetchData` hazard as the `unitRefreshTrigger` effect below: this effect's
  // own body clears its guard (`navigate(..., { state: {} })` wipes `location.state.refreshUnits`),
  // so today it only ever re-fires once per navigation rather than looping. But that safety
  // depends entirely on `state: {}` never being dropped from that `navigate` call, so -- to
  // remove the "unstable callback in a dependency array" hazard outright rather than rely on
  // that guard staying intact -- depend only on the value that actually gates the effect body.
  useEffect(() => {
    if (location.state?.refreshUnits) {
      fetchData(1);
      refreshCounts();
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.refreshUnits]);

  // NOTE: `fetchData` (from `infiniteScrollHook`) is NOT referentially stable -- it is a
  // plain function recreated on every render of that hook, not wrapped in `useCallback`.
  // Including it (or `refreshCounts`, which IS stable) in this effect's dependency array
  // caused an infinite fetch loop in production: fetchData/refreshCounts trigger state
  // updates -> re-render -> new `fetchData` reference -> effect re-fires -> repeat forever,
  // once a parent (e.g. Property Details -> Units) bumped `unitRefreshTrigger` above 0 and
  // never reset it. Depend ONLY on `unitRefreshTrigger` (matching pre-refactor `dev`
  // behavior) so this effect fires exactly once per trigger bump; `fetchData`/`refreshCounts`
  // are read from the closure in effect at the time the trigger changes, which is correct
  // since both are recreated fresh on every render anyway.
  useEffect(() => {
    if (unitRefreshTrigger !== undefined && unitRefreshTrigger > 0) {
      fetchData(1);
      refreshCounts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitRefreshTrigger]);

  const handleSort = (dir: "asc" | "desc") => setSortDirection(dir);
  const handleFilterChange = (filter: string) => setActiveFilter(filter);

  const fetchUnitForEdit = async (unitId: number) => {
    try {
      const res = await apiHelper(UnitService.getApiUnit1, { showNotification: false }, unitId);
      if (res?.data?.success) { setSelectedUnit(res.data.success); setModalOpen(true); }
    } catch (err) { console.error(err); }
  };

  const handleModalClose = () => { setModalOpen(false); setSelectedUnit(undefined); };

  const handleUnitMutated = useCallback(() => {
    fetchData(1);
    refreshCounts();
    if (onUnitAdded) onUnitAdded();
  }, [fetchData, onUnitAdded, refreshCounts]);

  return (
    <Routes>
      <Route
        index
        element={
          <PermissionGuard permission="Unit" level={AccessLevelEnum.ReadOnly} fallback={<NoAccess />}>
            <div className="content-wrapper container-fluid">
              {hasAnyUnits && (
                <UnitTopHeader
                  PageHeading="Units"
                  ButtonName="Add Unit"
                  searchTerm={searchTerm}
                  setSearchTerm={setSearchTerm}
                  handleSort={handleSort}
                  currentSortDirection={sortDirection}
                  handleFilterChange={handleFilterChange}
                  activeFilter={activeFilter}
                  filterOptions={visibleFilterOptions}
                  onClick={() => { setSelectedUnit(undefined); setModalOpen(true); }}
                  showAddButton={canWrite("Unit")}
                />
              )}
              <UnitCard
                Id="unit"
                className="no-side-margin"
                IsFooter={true}
                hasAnyData={hasAnyUnits}
                setModalOpen={setModalOpen}
                dataHookState={{ rows, loading, hasMore, fetchData, currentPage, onRefresh: handleUnitMutated }}
                onEditUnit={(unit) => fetchUnitForEdit(unit.id)}
                activeTab={activeFilter}
              />
              {isModalOpen && (
                <AddUnitForm
                  isModalOpen={isModalOpen}
                  setModalOpen={handleModalClose}
                  fetchData={handleUnitMutated}
                  unitData={selectedUnit}
                  isProperty={isEntity}
                  propertyId={entityId}
                />
              )}
            </div>
          </PermissionGuard>
        }
      />
      <Route
        path="/unitdetails/:id"
        element={
          <PermissionGuard permission="Unit" level={AccessLevelEnum.ReadOnly} fallback={<NoAccess />}>
            <UnitDetails2 />
          </PermissionGuard>
        }
      />
    </Routes>
  );
};

export default Units;